//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  BulkCreateOrderResponse,
  CreatorsCampaignsBulkCreateOrderRequest,
  CreatorsCampaignsCreateOrderRequest,
  CreatorsCampaignsCreateOrderResponse,
  CreatorsCampaignsOrderDetails,
  CreatorsCampaignsOrderDetailsRequest,
  CreatorsCampaignsOrdersListQueryRequest,
  CreatorsCampaignsOrdersListResponse,
} from '@apis/creators-campaigns/creators-orders/CreatorOrdersApiModel';
import {
  deserializeBulkCreateOrderResponse,
  deserializeCreatorsCampaignsCreatedOrderData,
  deserializeCreatorsCampaignsLatestOrderData,
  deserializeCreatorsCampaignsOrderDetailsData,
} from '@apis/creators-campaigns/creators-orders/CreatorsOrderApiUtils';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const CREATOR_CAMPAIGNS_DETAILS_DEFAULT = '/api/v1/campaigns';

const creatorsCampaignsFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'affable_gateway',
    `${CREATOR_CAMPAIGNS_DETAILS_DEFAULT}${endpoint}`,
    init,
  );
};

/**
 * GET /api/v1/campaigns/:campaignSlugName/orders
 *
 * Get Creators Campaigns orders list by campaignSlugName
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.pageNumber - The page number for pagination
 * @param req.pageSize - The page size for pagination
 * @param req.searchTerm - The search term for searching of campaign orders
 * @param req.sortCriteria - sortCriteria one of the sorting fields
 * @param req.sortDirection - sortDirection either ascending or descending
 */
export const getCreatorsCampaignsOrdersList = async (
  req: CreatorsCampaignsOrdersListQueryRequest,
  init?: RequestInit,
): Promise<CreatorsCampaignsOrdersListResponse> => {
  const { campaignSlugName, ...reqParams } = req;

  const params = makeParams(reqParams);
  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/orders?${params}`,
    {
      ...init,
      method: 'GET',
    },
  );

  const campaignOrdersListResults = await res.json<{
    data: CreatorsCampaignsOrdersListResponse;
  }>((res) => ({
    data: deserializeCreatorsCampaignsLatestOrderData(res.data),
  }));

  return campaignOrdersListResults.data;
};

/**
 * GET /api/v1/campaigns/${campaignSlugName}/orders/${orderId}
 *
 * Get Order Details by campaignSlugName and orderId
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.orderId - The unique identifier for the orders
 */

export const getCreatorsCampaignsOrderDetails = async (
  req: CreatorsCampaignsOrderDetailsRequest,
  init?: RequestInit,
): Promise<CreatorsCampaignsOrderDetails> => {
  const res = await creatorsCampaignsFetch(
    `/${req.campaignSlugName}/orders/${req.orderId}?`,
    {
      ...init,
      method: 'GET',
    },
  );

  const campaignOrderDetails = await res.json<{
    data: CreatorsCampaignsOrderDetails;
  }>((res) => ({
    data: deserializeCreatorsCampaignsOrderDetailsData(res.data),
  }));
  return campaignOrderDetails.data;
};

/**
 * POST /api/v1/campaigns/${campaignSlugName}/orders
 *
 * Create Order for a campaignSlugName and influencerId
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.influencerId - The id of the influencer
 * @param req.selectedVariantIds - The ids of products that we want to be part of the order
 */

export const createCreatorsCampaignsOrder = async (
  req: CreatorsCampaignsCreateOrderRequest,
  init?: RequestInit,
): Promise<CreatorsCampaignsCreateOrderResponse> => {
  const { campaignSlugName, ...body } = req;
  const res = await creatorsCampaignsFetch(`/${campaignSlugName}/orders`, {
    ...init,
    method: 'POST',
    body: JSON.stringify(body),
  });

  const createdOrderDetails = await res.json<{
    data: CreatorsCampaignsCreateOrderResponse;
  }>((res) => ({
    data: deserializeCreatorsCampaignsCreatedOrderData(res.data),
  }));
  return createdOrderDetails.data;
};

/**
 * POST /api/v1/campaigns/${campaignSlugName}/orders/bulk
 *
 * Bulk Create Orders for multiple creators in a campaign
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.creators - Array of creators with their selected variant IDs
 */
export const createCreatorsCampaignsBulkOrder = async (
  req: CreatorsCampaignsBulkCreateOrderRequest,
  init?: RequestInit,
): Promise<BulkCreateOrderResponse> => {
  const { campaignSlugName, creators } = req;

  const res = await creatorsCampaignsFetch(`/${campaignSlugName}/orders/bulk`, {
    ...init,
    method: 'POST',
    body: JSON.stringify({ creators }),
  });

  const response = await res.json<{
    data: BulkCreateOrderResponse;
  }>(deserializeBulkCreateOrderResponse);

  return response.data;
};
